import React, {ReactElement, ReactNode, useState} from "react";
import {LocalImage, LocalImagePicker} from "./LocalImagePicker";
import {ErrorMessage} from "../ErrorMessage";
import {FormattedMessage} from "react-intl";
import {Spinner} from "../Spinner";
import {SecondaryButton} from "../SecondaryButton";
import {PrimaryButton} from "../PrimaryButton";
import {imageApi} from "lib/api/wineMenuApi";
import {ReactComponent as WarningIcon} from "components/Icons/warning.svg";
import {ImageResizer} from "./ImageResizer";

interface ImageValidationResult {
    valid: boolean
    title?: ReactNode
    text?: ReactNode
}

type ImageValidatioFunction = (image: LocalImage) => ImageValidationResult;

export interface ImageUploaderProps {
    userId: string
    collection: string
    onSuccess: (newImageUrl: string) => void
    onCancel: () => void
    validate?: ImageValidatioFunction
    allowResize?: boolean
    resizeAspect?: number
}

export function ImageUploader(props: ImageUploaderProps): ReactElement {
    const [image, setImage] = useState<LocalImage | null>(null);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [imageResizerOpen, setImageResizerOpen] = useState<boolean>(false);

    const validationResult = applyImageValidation(props.validate, image);
    const uploadButtonDisabled = pending || image == null || validationResult?.valid !== true;

    const cancel = () => {
        setImage(null);
        setPending(false);
        setError(null);
        props.onCancel();
    }

    const startResize = () => {
        setImageResizerOpen(true);
    }

    const onResize = (image: LocalImage) => {
        setImage(image);
        cancelResize();
    }

    const cancelResize = () => {
        setImageResizerOpen(false);
    }

    const uploadImage = async () => {
        if (image == null) {
            return;
        }

        setPending(true);
        setError(null);

        try {
            const response = await imageApi.uploadImageForUser({
                userId: props.userId,
                body: image.binaryData,
                collection: props.collection
            });
            setPending(false);
            setImage(null);
            setError(null);

            props.onSuccess(response.data.url);
        } catch (e: any) {
            setPending(true);
            setError(e.toString);
        }
    };


    return (
        <div className="flex flex-col gap-5">
            <div className="flex-auto flex flex-col gap-5">

                {imageResizerOpen&&image&&
                    <ImageResizer
                        image={image}
                        onClose={cancelResize}
                        onResize={onResize}
                        aspect={props.resizeAspect}
                    />
                }

                <div className="h-48 w-full">
                    <LocalImagePicker
                        onPickImage={setImage}
                        image={image}
                    />
                </div>

                {(validationResult != null && (validationResult.title || validationResult.text)) && (
                    <div className="bg-accent2 py-3 px-4 gap-2 flex items-start">
                        <div className="text-primary">
                            <WarningIcon />
                        </div>
                        <div>
                            {validationResult.title&&<h2 className="text-lg font-medium leading-none">{validationResult.title}</h2>}
                            {validationResult.text&&<p className="py-1 leading-tight">{validationResult.text}</p>}
                        </div>
                    </div>
                )}

                {error && (
                    <ErrorMessage>
                        <FormattedMessage
                            id="management.rename.modal.error"
                            values={{message: error}}
                        />
                    </ErrorMessage>
                )}
            </div>

            <div className="flex-none flex gap-2 justify-end items-center">
                {pending && (
                    <Spinner className="flex-none w-8 h-8 inline-block"/>
                )}

                {props.allowResize && image && <SecondaryButton onClick={startResize}>
                    <FormattedMessage id="editor.image.upload.resize"/>
                </SecondaryButton>}

                <SecondaryButton onClick={cancel}>
                    <FormattedMessage id="editor.image.upload.cancel"/>
                </SecondaryButton>

                <PrimaryButton
                    disabled={uploadButtonDisabled}
                    onClick={uploadImage}
                >
                    <FormattedMessage id="editor.image.upload.upload"/>
                </PrimaryButton>
            </div>
        </div>
    );
}

function applyImageValidation(validationFn: ImageValidatioFunction | null | undefined, image: LocalImage | null): ImageValidationResult | null {
    if (image == null) {
        return null;
    }

    if (validationFn == null) {
        return {valid: true};
    }

    return validationFn(image);
}
